import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { verdictDisplay } from "@/lib/displayLabels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RebalanceControls } from "@/components/RebalancePanel";
import {
  Plus, Trash2, RefreshCw, Briefcase, Shield, Zap, Target, Activity,
  CheckCircle, AlertTriangle, RotateCw, Play, ChevronDown, ChevronUp, Compass, Gauge, Pencil, ArrowRightLeft, Lock
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { useIsPro } from "@/hooks/useIsPro";
import AdjustPanel from "./AdjustPanel";

const STRATEGY_TYPES = [
  "bull_put_spread", "bear_call_spread", "bull_call_spread", "bear_put_spread",
  "covered_call", "cash_secured_put", "iron_condor", "iron_butterfly",
  "long_call", "long_put", "short_call", "short_put",
  "collar", "protective_put", "straddle", "strangle",
  "risk_reversal", "calendar_spread", "diagonal_spread"
];
const SECTOR_COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#84CC16"];
// Iter133: PIESection extracted to /components/PIESection.jsx. Re-exported
// from here to preserve the existing `import { PIESection } from "@/pages/PortfolioPage"`
// path used by WheelIncomePage.js.
import { PIESection } from "@/components/PIESection";
import { BrokerTruthReconciliation } from "@/components/BrokerTruthReconciliation";
export { PIESection };


import { RRGChart } from "@/components/RRGChart";

// ═══ Sector Rotation Monitor (moved from Dashboard) ═══
function SectorRotationPanel() {
  // Iter227 — two datasets: sector ETFs vs 10 largest US mega-caps.
  const [group, setGroup] = useState("sectors"); // sectors | megacaps
  const [dataByGroup, setDataByGroup] = useState({}); // group -> payload
  const [loading, setLoading] = useState(false);
  // Iter225 — click-to-isolate: selected sector highlighted, rest dimmed.
  const [focusSector, setFocusSector] = useState(null);

  const load = async (g = group) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/sector-rotation?group=${g}`);
      setDataByGroup(prev => ({ ...prev, [g]: d }));
    }
    catch (e) { console.warn(e); }
    finally { setLoading(false); }
  };

  const switchGroup = (g) => {
    setGroup(g);
    setFocusSector(null);
    if (!dataByGroup[g]) load(g); // lazy: mega-caps only fetched when opened
  };

  // Iter226 — auto-load on visit (same pattern as Dashboard's Performance vs
  // S&P 500). "Scan Sectors" stays as a manual mid-session refresh.
  useEffect(() => { load("sectors"); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const data = dataByGroup[group];

  const signalColors = { STRENGTHENING: "#10B981", NEUTRAL: "#71717A", WEAKENING: "#EF4444", "N/A": "#71717A" };
  // Iter224 — single 3-point scale. The label is driven ONLY by RS-Momentum
  // (trailing 4-week acceleration of relative strength vs SPY) — the same
  // number as the RRG chart's Y-axis, so table and chart cannot disagree.
  const signalDisplayLabel = {
    STRENGTHENING: "Strengthening",
    NEUTRAL: "Neutral",
    WEAKENING: "Weakening",
    "N/A": "N/A",
  };
  const phaseLabels = { early_expansion: "Early Expansion", late_expansion: "Late Expansion", mid_cycle: "Mid Cycle", early_contraction: "Early Contraction", late_contraction: "Late Contraction" };
  const phaseColor = { early_expansion: "#10B981", late_expansion: "#F59E0B", mid_cycle: "#3B82F6", early_contraction: "#EF4444", late_contraction: "#EF4444" };
  const rot = data?.rotation || {};
  const sectors = data?.sectors || {};

  return (
    <div className="space-y-3" data-testid="sector-rotation-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {/* Iter227 — dataset toggle */}
          <div className="flex items-center border rounded-sm overflow-hidden" style={{ borderColor: "var(--hoip-border)" }} data-testid="rotation-group-toggle">
            {[["sectors", "Sectors"], ["megacaps", "Mega-Caps"], ["portfolio", "My Portfolio"]].map(([g, label]) => (
              <button key={g} type="button" onClick={() => switchGroup(g)}
                className="h-6 px-2.5 text-[10px] font-bold transition-colors"
                style={group === g
                  ? { background: "var(--hoip-hover)", color: "var(--hoip-text-primary)" }
                  : { color: "var(--hoip-text-tertiary)" }}
                data-testid={`rotation-group-${g}`}>
                {label}
              </button>
            ))}
          </div>
          {rot.phase && <span className="text-[10px] px-1.5 py-0.5 rounded-sm font-bold" style={{ background: `${phaseColor[rot.phase]}15`, color: phaseColor[rot.phase] }}>{phaseLabels[rot.phase]}</span>}
          {rot.accumulate?.length > 0 && <span className="text-[9px] text-emerald-500 font-mono-data">↑{rot.accumulate.length} strengthening</span>}
          {rot.reduce?.length > 0 && <span className="text-[9px] text-red-400 font-mono-data">↓{rot.reduce.length} weakening</span>}
        </div>
        <Button size="sm" onClick={() => load()} disabled={loading} className="h-7 text-[10px] bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-3" data-testid="rotation-scan-btn">
          {loading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Compass className="w-3 h-3 mr-1" />} {group === "megacaps" ? "Scan Mega-Caps" : group === "portfolio" ? "Scan My Tickers" : "Scan Sectors"}
        </Button>
      </div>

      {rot.rationale && (
        <div className="text-[11px] px-2 py-1.5 border rounded-sm space-y-1"
          style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)", color: "var(--hoip-text-secondary)" }}
          data-testid="rotation-rationale">
          <div>
            <span className="font-bold text-[10px] mr-1" style={{ color: "var(--hoip-text-tertiary)" }}>
              Analytics observation:
            </span>
            {/* Iter176 — remediation §B.18 requires the rationale be
                framed as descriptive commentary, not a recommendation.
                We display the backend text verbatim (data untouched)
                but prefix + suffix it so the framing satisfies FCA
                financial-promotion + CMVM Art. 295 exposure. */}
            {(rot.rationale || "")
              .replace(/^Increase\s+/i, "Outperformance in ")
              .replace(/^Decrease\s+/i, "Underperformance in ")
              .replace(/\bpotential contraction\b/gi, "historically consistent with early-contraction phases")
              .replace(/\bactivate hedges\b/gi, "review hedge thresholds")}
          </div>
          <div className="text-[9px] italic" style={{ color: "var(--hoip-text-tertiary)" }}>
            Descriptive analytics — not a recommendation to buy or sell any sector or security.
          </div>
        </div>
      )}

      {Object.keys(sectors).length > 0 && (
        <>
          {/* Iter224 — RRG quadrant chart (primary view) */}
          <RRGChart sectors={sectors} focus={focusSector} onFocus={setFocusSector} />

          <div className="space-y-0.5" data-testid="rotation-heatmap">
          <div className="grid grid-cols-[140px_60px_60px_55px_80px_1fr] gap-1 text-[9px] font-medium px-1 pb-1" style={{ color: "var(--hoip-text-tertiary)" }}>
            <span>{group === "megacaps" ? "Company" : group === "portfolio" ? "Ticker" : "Sector"}</span><span className="text-right">1M</span><span className="text-right">3M</span><span className="text-right">Excess</span><span className="text-center">RS Trend</span><span className="text-center">RS-Momentum (4-wk)</span>
          </div>
          {Object.entries(sectors).sort((a, b) => (b[1].rs_momentum || 0) - (a[1].rs_momentum || 0)).map(([sector, v]) => {
            const mom = v.rs_momentum;
            const momDev = mom != null ? mom - 100 : 0;
            const barColor = signalColors[v.signal] || "#71717A";
            return (
            <div key={sector}
              onClick={() => setFocusSector(focusSector === sector ? null : sector)}
              className="grid grid-cols-[140px_60px_60px_55px_80px_1fr] gap-1 items-center text-[10px] px-1 py-0.5 border-b cursor-pointer transition-opacity"
              style={{
                borderColor: "var(--hoip-divider)",
                opacity: focusSector && focusSector !== sector ? 0.35 : 1,
                background: focusSector === sector ? "var(--hoip-hover)" : "transparent",
              }}
              data-testid={`rotation-row-${v.etf}`}>
              <span className="truncate" style={{ color: "var(--hoip-text-secondary)" }}>
                <span className="font-mono-data text-[9px] mr-1" style={{ color: "var(--hoip-text-tertiary)" }}>{v.etf}</span>{sector}
              </span>
              <span className={`text-right font-mono-data ${(v.ret_1m || 0) >= 0 ? "text-emerald-500" : "text-red-400"}`}>{(v.ret_1m || 0) >= 0 ? "+" : ""}{v.ret_1m || 0}%</span>
              <span className={`text-right font-mono-data ${(v.ret_3m || 0) >= 0 ? "text-emerald-500" : "text-red-400"}`}>{(v.ret_3m || 0) >= 0 ? "+" : ""}{v.ret_3m || 0}%</span>
              <span className={`text-right font-mono-data ${(v.excess_1m || 0) >= 0 ? "text-emerald-500" : "text-red-400"}`}>{(v.excess_1m || 0) >= 0 ? "+" : ""}{v.excess_1m || 0}%</span>
              <span className="text-center"><span className="px-1 py-0.5 rounded-sm text-[9px] font-bold" style={{ color: barColor, background: `${barColor}15` }}>{signalDisplayLabel[v.signal] || v.signal || "N/A"}</span></span>
              <div className="flex items-center gap-1" title="RS-Momentum: 4-week rate of change of relative strength vs SPY (100 = flat). This single number drives the RS Trend label and the chart's Y-axis.">
                <div className="flex-1 h-1.5 rounded-full overflow-hidden relative" style={{ background: "var(--hoip-bg)" }}>
                  <div className="absolute top-0 bottom-0 w-px z-10" style={{ left: "50%", background: "var(--hoip-border)" }} />
                  <div className="absolute top-0 bottom-0" style={{
                    left: momDev >= 0 ? "50%" : `${Math.max(2, 50 - Math.abs(momDev) * 16)}%`,
                    width: `${Math.min(48, Math.max(1, Math.abs(momDev) * 16))}%`,
                    background: barColor,
                  }} />
                </div>
                <span className="text-[8px] w-12 text-right font-mono-data" style={{ color: barColor }}>{mom != null ? `${momDev >= 0 ? "+" : ""}${momDev.toFixed(1)}` : "—"}</span>
              </div>
            </div>
          );})}
          </div>

          {/* Iter224 — methodology disclosure (same tier as other in-app disclaimers) */}
          <div className="text-[9px] leading-relaxed px-2 py-1.5 border rounded-sm"
            style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)", color: "var(--hoip-text-tertiary)" }}
            data-testid="rotation-methodology-note">
            <span className="font-bold">Methodology:</span> RS-Ratio = sector ETF ÷ SPY price ratio, normalized to 100
            by its trailing ~3-month mean. RS-Momentum = 4-week rate of change of RS-Ratio; this single number drives
            the RS Trend label (≥ +0.4 Strengthening · ≤ −0.4 Weakening · else Neutral) and the chart's Y-axis, so the
            table and chart always agree. 1M / 3M are the sector's own raw returns (not RS-based); Excess = sector 1M
            return − SPY 1M return. <span className="italic">Descriptive market-internals data — not a trading signal
            or a recommendation to buy or sell any sector or security; the predictive value of sector rotation is
            academically debated.</span>
          </div>
        </>
      )}
      {!data && !loading && <div className="text-center py-4 text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }}>Loading sector relative strength vs S&P 500…</div>}
      {!data && loading && <div className="text-center py-4 text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }}><RefreshCw className="w-3 h-3 animate-spin inline mr-1" />Analyzing relative strength vs S&P 500…</div>}
    </div>
  );
}

// ═══ Rebalancing Section (moved from Dashboard) ═══
function RebalancingPanel() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [mvoData, setMvoData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mvoMode, setMvoMode] = useState("standard"); // standard | wheel

  const load = async () => {
    setLoading(true);
    try {
      const endpoints = [
        api.get("/portfolio/rebalancing"),
        api.get("/portfolio/mvo-rebalancing"),
      ];
      if (mvoMode === "wheel") {
        endpoints.push(api.get("/portfolio/wheel-aware-rebalancing"));
      }
      const results = await Promise.all(endpoints);
      setData(results[0].data);
      setMvoData(results[1].data);
      if (mvoMode === "wheel" && results[2]) {
        setData(prev => ({ ...prev, wheelSuggestions: results[2].data.suggestions || [] }));
      }
    } catch (e) { console.warn(e); }
    finally { setLoading(false); }
  };

  const statusColors = { overweight: "#EF4444", underweight: "#F59E0B", balanced: "#10B981" };
  const actionColors = { TRIM: "#EF4444", ADD: "#10B981", CONCENTRATION: "#F59E0B" };
  const mvo = mvoData?.mvo;
  const currentWeights = mvoData?.current_weights || {};
  const mvoWeights = mvo?.weights || {};
  const tickers = mvoData?.tickers || [];

  return (
    <div className="space-y-3" data-testid="rebalancing-panel">
      <div className="flex items-center justify-between">
        <div className="flex gap-0.5 border rounded-sm p-0.5" style={{ borderColor: "var(--hoip-border)" }}>
          <button onClick={() => setMvoMode("standard")}
            className={`px-3 py-1 text-[10px] rounded-sm transition-colors ${mvoMode === "standard" ? "bg-blue-600 text-white" : "text-[var(--hoip-text-tertiary)]"}`}
            data-testid="mvo-standard">Standard MVO</button>
          <button onClick={() => setMvoMode("wheel")}
            className={`px-3 py-1 text-[10px] rounded-sm transition-colors ${mvoMode === "wheel" ? "bg-blue-600 text-white" : "text-[var(--hoip-text-tertiary)]"}`}
            data-testid="mvo-wheel">Wheel-Aware MVO</button>
        </div>
        <Button size="sm" onClick={load} disabled={loading} className="h-7 text-[10px] bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-3" data-testid="rebalancing-analyze-btn">
          {loading ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Activity className="w-3 h-3 mr-1" />} Analyze
        </Button>
      </div>

      {/* Wheel-Aware Suggestions */}
      {mvoMode === "wheel" && data?.wheelSuggestions?.length > 0 && (
        <div className="space-y-1.5" data-testid="wheel-aware-suggestions">
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Wheel-Aware Actions</span>
          {data.wheelSuggestions.map((s, i) => (
            <div key={i} className="flex items-start gap-2 text-[10px] border rounded-sm p-2" style={{ borderColor: "var(--hoip-border)" }}>
              <span className={`px-1.5 py-0.5 rounded-sm font-bold text-[9px] whitespace-nowrap ${
                s.action === "WRITE_CALLS" ? "bg-blue-500/10 text-blue-400" :
                s.action === "ENTER_VIA_CSP" ? "bg-emerald-500/10 text-emerald-400" :
                "bg-red-500/10 text-red-400"
              }`}>{s.action.replace(/_/g, " ")}</span>
              {s.ticker && <span className="font-mono-data font-medium text-blue-400 cursor-pointer hover:underline" onClick={() => navigate(`/ticker/${s.ticker}`)}>{s.ticker}</span>}
              <span className="flex-1" style={{ color: "var(--hoip-text-secondary)" }}>{s.reason}</span>
            </div>
          ))}
        </div>
      )}

      {/* Sector Weights */}
      {data?.sector_analysis?.length > 0 && (
        <div className="space-y-1" data-testid="sector-weights">
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Sector Weights vs Target</span>
          {data.sector_analysis.filter(s => s.current_weight > 0 || s.target_weight >= 5).map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px]">
              <span className="w-28 truncate" style={{ color: "var(--hoip-text-tertiary)" }}>{s.sector}</span>
              <div className="flex-1 relative h-3 rounded-sm overflow-hidden" style={{ background: "var(--hoip-bg)" }}>
                <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${Math.min(s.current_weight * 2, 100)}%`, background: statusColors[s.status], opacity: 0.7 }} />
                <div className="absolute inset-y-0 border-l-2" style={{ left: `${Math.min(s.target_weight * 2, 100)}%`, borderColor: "var(--hoip-text-primary)" }} />
              </div>
              <span className="w-16 text-right font-mono-data" style={{ color: statusColors[s.status] }}>{s.current_weight}%<span className="text-[8px]">/{s.target_weight}%</span></span>
            </div>
          ))}
        </div>
      )}

      {/* Suggestions */}
      {data?.suggestions?.length > 0 && (
        <div className="space-y-1.5" data-testid="rebalancing-suggestions">
          <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Suggestions</span>
          {data.suggestions.map((s, i) => (
            <div key={i} className="flex items-start gap-2 text-[10px] border rounded-sm p-2" style={{ borderColor: "var(--hoip-border)" }}>
              <span className="px-1.5 py-0.5 rounded-sm font-bold text-[9px]" style={{ color: actionColors[s.action], background: `${actionColors[s.action]}10` }}>{s.action}</span>
              {s.ticker && <span className="font-mono-data font-medium text-blue-400 cursor-pointer hover:underline" onClick={() => navigate(`/ticker/${s.ticker}`)}>{s.ticker}</span>}
              <span className="flex-1" style={{ color: "var(--hoip-text-secondary)" }}>{s.reason}</span>
              {s.shares > 0 && <span className="font-mono-data" style={{ color: "var(--hoip-text-tertiary)" }}>{s.shares} sh (~${s.est_value?.toLocaleString()})</span>}
            </div>
          ))}
        </div>
      )}

      {/* MVO Optimal Weights */}
      {mvo && tickers.length > 0 && (
        <div className="border rounded-sm p-3" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }} data-testid="mvo-weights">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>MVO Optimal vs Current</span>
            <div className="flex items-center gap-3 text-[9px]">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "#3B82F6" }} /> Current</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "#10B981" }} /> Optimal</span>
            </div>
          </div>
          {tickers.map((t) => {
            const cur = currentWeights[t] || 0;
            const opt = mvoWeights[t] || 0;
            const diff = opt - cur;
            return (
              <div key={t} className="flex items-center gap-2 text-[10px] mb-1">
                <span className="w-12 font-mono-data font-medium" style={{ color: "var(--hoip-text-primary)" }}>{t}</span>
                <div className="flex-1 relative h-4 rounded-sm overflow-hidden" style={{ background: "var(--hoip-card)" }}>
                  <div className="absolute inset-y-0 left-0 rounded-sm" style={{ width: `${Math.min(cur, 100)}%`, background: "#3B82F6", opacity: 0.5 }} />
                  <div className="absolute inset-y-0 left-0 border-r-2 rounded-sm" style={{ width: `${Math.min(opt, 100)}%`, borderColor: "#10B981" }}>
                    <div className="h-full rounded-sm" style={{ background: "#10B981", opacity: 0.3 }} />
                  </div>
                </div>
                <span className="w-20 text-right font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>
                  {cur.toFixed(1)}% <span style={{ color: diff > 0 ? "#10B981" : diff < 0 ? "#EF4444" : "var(--hoip-text-tertiary)" }}>{diff > 0 ? "+" : ""}{diff.toFixed(1)}</span>
                </span>
              </div>
            );
          })}
          {mvo.reason && <div className="text-[9px] mt-1" style={{ color: "var(--hoip-text-tertiary)" }}>{mvo.reason}</div>}
        </div>
      )}

      {data && data.suggestions?.length === 0 && <div className="text-center py-4 text-[11px] text-emerald-500">Portfolio is well-balanced across sectors.</div>}
      {!data && !loading && <div className="text-center py-4 text-[11px]" style={{ color: "var(--hoip-text-tertiary)" }}>Click <strong>Analyze</strong> to check sector allocation and MVO optimal weights.</div>}
    </div>
  );
}

export default function PortfolioPage() {
  const [positions, setPositions] = useState({ stocks: [], options: [] });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [posRegimes, setPosRegimes] = useState({});
  const [showStockDialog, setShowStockDialog] = useState(false);
  const [showOptionsDialog, setShowOptionsDialog] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [adjustTicker, setAdjustTicker] = useState(null);
  const [pendingReconcile, setPendingReconcile] = useState(0);
  const { isPro, requirePro } = useIsPro();
  const [stockForm, setStockForm] = useState({ ticker: "", shares: "", avg_cost: "", account_type: "reg_t", purchase_date: "" });
  const [optionsForm, setOptionsForm] = useState({ ticker: "", strategy_type: "bull_put_spread", short_strike: "", long_strike: "", expiry: "", qty: "1", premium: "", notes: "" });

  const load = async () => {
    try {
      const [posRes, sumRes] = await Promise.all([
        api.get("/portfolio/positions/enriched"),
        api.get("/portfolio/summary"),
      ]);
      setPositions(posRes.data);
      setSummary(sumRes.data);
      // Fetch regimes for stock tickers
      const tickers = [...new Set((posRes.data.stocks || []).map(p => p.ticker))];
      tickers.forEach(t => {
        api.get(`/regime/${t}`).then(({ data: rd }) => {
          setPosRegimes(prev => ({ ...prev, [t]: rd }));
        }).catch(() => {});
      });
    } catch (err) { console.error("Portfolio load error:", err); }
    finally { setLoading(false); }
    // Iter202 — imported open trades not yet reflected here (pre-fix imports).
    api.get("/trade-log/import/pending-reconcile")
      .then(({ data: pr }) => setPendingReconcile(pr.pending || 0))
      .catch(() => setPendingReconcile(0));
  };

  useEffect(() => { load(); }, []);

  const handleAddStock = async () => {
    try {
      const payload = { ticker: stockForm.ticker.toUpperCase(), shares: parseInt(stockForm.shares), avg_cost: parseFloat(stockForm.avg_cost), account_type: stockForm.account_type, purchase_date: stockForm.purchase_date || null };
      if (editingStock) {
        await api.put(`/portfolio/stock-positions/${editingStock.id}`, payload);
      } else {
        await api.post("/portfolio/stock-positions", payload);
      }
      setShowStockDialog(false);
      setEditingStock(null);
      setStockForm({ ticker: "", shares: "", avg_cost: "", account_type: "reg_t", purchase_date: "" });
      load();
    } catch (err) { console.error("Stock save error:", err); }
  };

  const handleAddOptions = async () => {
    try {
      const payload = { ticker: optionsForm.ticker.toUpperCase(), strategy_type: optionsForm.strategy_type, short_strike: optionsForm.short_strike ? parseFloat(optionsForm.short_strike) : null, long_strike: optionsForm.long_strike ? parseFloat(optionsForm.long_strike) : null, expiry: optionsForm.expiry, qty: parseInt(optionsForm.qty), premium: parseFloat(optionsForm.premium || 0), notes: optionsForm.notes };
      if (editingOption) {
        await api.put(`/portfolio/options-positions/${editingOption.id}`, payload);
      } else {
        await api.post("/portfolio/options-positions", payload);
      }
      setShowOptionsDialog(false);
      setEditingOption(null);
      setOptionsForm({ ticker: "", strategy_type: "bull_put_spread", short_strike: "", long_strike: "", expiry: "", qty: "1", premium: "", notes: "" });
      load();
    } catch (err) { console.error("Options save error:", err); }
  };

  const editStock = (p) => {
    setEditingStock(p);
    setStockForm({ ticker: p.ticker, shares: p.shares?.toString() || "", avg_cost: p.avg_cost?.toString() || "", account_type: p.account_type || "reg_t", purchase_date: p.purchase_date || "" });
    setShowStockDialog(true);
  };

  const editOption = (p) => {
    setEditingOption(p);
    setOptionsForm({ ticker: p.ticker, strategy_type: p.strategy_type || "bull_put_spread", short_strike: p.short_strike?.toString() || "", long_strike: p.long_strike?.toString() || "", expiry: p.expiry || "", qty: p.qty?.toString() || "1", premium: p.premium?.toString() || "", notes: p.notes || "" });
    setShowOptionsDialog(true);
  };

  const handleDeleteStock = async (id) => { try { await api.delete(`/portfolio/stock-positions/${id}`); load(); } catch (err) { console.error(err); } };
  const handleDeleteOption = async (id) => { try { await api.delete(`/portfolio/options-positions/${id}`); load(); } catch (err) { console.error(err); } };

  if (loading) return (<div className="flex items-center justify-center h-full p-8"><RefreshCw className="w-5 h-5 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} /></div>);

  const sectorData = summary?.sector_exposure
    ? Object.entries(summary.sector_exposure).map(([name, value], i) => ({ name, value: Math.round(value), fill: SECTOR_COLORS[i % SECTOR_COLORS.length] }))
    : [];

  const reconcileFromTradeLog = async () => {
    try {
      const { data } = await api.post("/trade-log/import/reconcile");
      if (data.created > 0) {
        toast.success(`Reconciled ${data.created} position${data.created === 1 ? "" : "s"} from the Trade Log`, {
          description: `${data.created_stock} stock · ${data.created_options} options${data.skipped_manual_match ? ` · ${data.skipped_manual_match} skipped (manual match)` : ""}`,
        });
      } else {
        toast.info("Portfolio already in sync with the Trade Log", {
          description: `${data.open_imported} imported open trade(s) checked — ${data.already_linked} already linked${data.skipped_manual_match ? `, ${data.skipped_manual_match} matched manual entries` : ""}.`,
        });
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconciliation failed");
    }
  };

  return (
    <div className="p-3 md:p-6 space-y-3 md:space-y-4" data-testid="portfolio-page">
      {pendingReconcile > 0 && (
        <div className="border rounded-sm px-3 py-2 flex items-center gap-2 flex-wrap text-[11px]"
          style={{ borderColor: "rgba(245,158,11,0.4)", background: "rgba(245,158,11,0.08)", color: "#F59E0B" }}
          data-testid="portfolio-pending-reconcile-banner">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="font-bold">{pendingReconcile} imported open trade{pendingReconcile === 1 ? "" : "s"} not yet reflected in Portfolio.</span>
          <span style={{ color: "var(--hoip-text-secondary)" }}>
            Press “Reconcile now” to sync them — manually entered positions are never duplicated.
          </span>
          <button onClick={reconcileFromTradeLog} className="ml-auto px-1.5 py-0.5 border rounded-sm text-[10px] font-bold hover:bg-[rgba(245,158,11,0.12)]"
            style={{ borderColor: "rgba(245,158,11,0.4)" }} data-testid="pending-reconcile-run-btn">
            <RotateCw className="w-2.5 h-2.5 inline mr-1" />Reconcile now
          </button>
        </div>
      )}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl md:text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Portfolio</h1>
        {positions.broker_linked && positions.broker_source && (
          <span data-testid="portfolio-broker-banner" className="text-[10px] tracking-widest font-bold px-1.5 py-0.5"
            style={{ background: "rgba(0,200,150,0.12)", color: "#00C896", border: "1px solid rgba(0,200,150,0.35)" }}>
            HOLDINGS · {positions.broker_source.toUpperCase()}
          </span>
        )}
        {positions.broker_error && (
          <span data-testid="portfolio-broker-error" className="text-[10px] tracking-widest font-bold px-1.5 py-0.5"
            title={positions.broker_error}
            style={{ background: "rgba(245,158,11,0.18)", color: "#F59E0B", border: "1px solid rgba(245,158,11,0.35)" }}>
            BROKER UNAVAILABLE · using local
          </span>
        )}
      </div>

      <Tabs defaultValue={new URLSearchParams(window.location.search).get("tab") || "positions"} className="w-full">
        <TabsList className="bg-[#111111] border border-[#27272A] rounded-sm h-9">
          <TabsTrigger value="positions" className="text-xs rounded-sm data-[state=active]:bg-[#1A1A1A]" data-testid="tab-positions">Positions</TabsTrigger>
          <TabsTrigger value="pie" className="text-xs rounded-sm data-[state=active]:bg-[#1A1A1A]" data-testid="tab-pie">Income Engine</TabsTrigger>
          <TabsTrigger value="rotation" className="text-xs rounded-sm data-[state=active]:bg-[#1A1A1A]" data-testid="tab-rotation">Sector Rotation</TabsTrigger>
          <TabsTrigger value="rebalancing" className="text-xs rounded-sm data-[state=active]:bg-[#1A1A1A]" data-testid="tab-rebalancing">Rebalancing</TabsTrigger>
        </TabsList>

        <TabsContent value="positions" className="mt-4 space-y-4">
          {/* Stock Positions */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>{positions.stocks.length} stocks, {positions.options.length} options</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={reconcileFromTradeLog}
                className="h-8 rounded-sm text-xs border-[#27272A]"
                title="Sync open positions from imported broker trades into Portfolio — manually entered positions are never duplicated. Safe to run anytime."
                data-testid="reconcile-from-tradelog-btn">
                <RotateCw className="w-3 h-3 mr-1" /> Reconcile from Trade Log
              </Button>
              <Dialog open={showStockDialog} onOpenChange={(open) => { setShowStockDialog(open); if (!open) { setEditingStock(null); setStockForm({ ticker: "", shares: "", avg_cost: "", account_type: "reg_t", purchase_date: "" }); } }}>
                <DialogTrigger asChild><Button size="sm" className="h-8 bg-white text-black hover:bg-gray-200 rounded-sm text-xs" data-testid="add-stock-btn"><Plus className="w-3 h-3 mr-1" /> Add Stock</Button></DialogTrigger>
                <DialogContent className="bg-[#111111] border-[#27272A] text-[#F4F4F5]" aria-describedby="stock-dialog-desc">
                  <DialogHeader><DialogTitle className="text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>{editingStock ? "Edit Stock Position" : "Add Stock Position"}</DialogTitle></DialogHeader>
                  <p id="stock-dialog-desc" className="sr-only">Form to add a stock position</p>
                  <div className="space-y-3 mt-2">
                    <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Ticker</Label><Input data-testid="stock-ticker-input" value={stockForm.ticker} onChange={(e) => setStockForm({...stockForm, ticker: e.target.value})} placeholder="AAPL" className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Shares</Label><Input data-testid="stock-shares-input" type="number" value={stockForm.shares} onChange={(e) => setStockForm({...stockForm, shares: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                      <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Avg Cost</Label><Input data-testid="stock-cost-input" type="number" step="0.01" value={stockForm.avg_cost} onChange={(e) => setStockForm({...stockForm, avg_cost: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                    </div>
                    <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Purchase Date</Label><Input data-testid="stock-purchase-date" type="date" value={stockForm.purchase_date} onChange={(e) => setStockForm({...stockForm, purchase_date: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                    <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Account Type</Label>
                      <Select value={stockForm.account_type} onValueChange={(v) => setStockForm({...stockForm, account_type: v})}><SelectTrigger className="h-8 bg-[#0A0A0A] border-[#27272A] text-xs rounded-sm" data-testid="stock-account-type"><SelectValue /></SelectTrigger><SelectContent className="bg-[#111111] border-[#27272A]"><SelectItem value="reg_t" className="text-xs text-[#F4F4F5]">Reg T</SelectItem><SelectItem value="portfolio_margin" className="text-xs text-[#F4F4F5]">Portfolio Margin</SelectItem></SelectContent></Select>
                    </div>
                    <Button onClick={handleAddStock} className="w-full bg-white text-black hover:bg-gray-200 rounded-sm h-8 text-xs" data-testid="submit-stock-btn">{editingStock ? "Save Changes" : "Add Position"}</Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={showOptionsDialog} onOpenChange={(open) => { setShowOptionsDialog(open); if (!open) { setEditingOption(null); setOptionsForm({ ticker: "", strategy_type: "bull_put_spread", short_strike: "", long_strike: "", expiry: "", qty: "1", premium: "", notes: "" }); } }}>
                <DialogTrigger asChild><Button size="sm" className="h-8 bg-white text-black hover:bg-gray-200 rounded-sm text-xs" data-testid="add-options-btn"><Plus className="w-3 h-3 mr-1" /> Add Options</Button></DialogTrigger>
                <DialogContent className="bg-[#111111] border-[#27272A] text-[#F4F4F5] max-w-md" aria-describedby="options-dialog-desc">
                  <DialogHeader><DialogTitle className="text-sm" style={{ fontFamily: "Chivo, sans-serif" }}>{editingOption ? "Edit Options Position" : "Add Options Position"}</DialogTitle></DialogHeader>
                  <p id="options-dialog-desc" className="sr-only">Form to add an options position</p>
                  <div className="space-y-3 mt-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Ticker</Label><Input data-testid="option-ticker-input" value={optionsForm.ticker} onChange={(e) => setOptionsForm({...optionsForm, ticker: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                      <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Strategy</Label>
                        <select value={optionsForm.strategy_type} onChange={(e) => setOptionsForm({...optionsForm, strategy_type: e.target.value})} className="w-full h-8 px-2 text-xs font-mono-data rounded-sm border outline-none appearance-none cursor-pointer capitalize" style={{ background: "#0A0A0A", borderColor: "#27272A", color: "#F4F4F5" }} data-testid="option-strategy-select">
                          {STRATEGY_TYPES.map(s => (<option key={s} value={s} style={{ background: "#111111", color: "#F4F4F5" }}>{s.replace(/_/g, " ")}</option>))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Short Strike</Label><Input data-testid="option-short-strike" type="number" step="0.5" value={optionsForm.short_strike} onChange={(e) => setOptionsForm({...optionsForm, short_strike: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                      <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Long Strike</Label><Input data-testid="option-long-strike" type="number" step="0.5" value={optionsForm.long_strike} onChange={(e) => setOptionsForm({...optionsForm, long_strike: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Expiry</Label><Input data-testid="option-expiry" type="date" value={optionsForm.expiry} onChange={(e) => setOptionsForm({...optionsForm, expiry: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                      <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Contracts</Label><Input data-testid="option-qty" type="number" value={optionsForm.qty} onChange={(e) => setOptionsForm({...optionsForm, qty: e.target.value})} className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                      <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Premium/sh</Label><Input data-testid="option-premium" type="number" step="0.01" value={optionsForm.premium} onChange={(e) => setOptionsForm({...optionsForm, premium: e.target.value})} placeholder="1.50" className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs font-mono-data" /></div>
                    </div>
                    <div><Label className="text-xs mb-1 block" style={{ color: "var(--hoip-text-secondary)" }}>Notes</Label><Input data-testid="option-notes" value={optionsForm.notes} onChange={(e) => setOptionsForm({...optionsForm, notes: e.target.value})} placeholder="Trade notes..." className="bg-[#0A0A0A] border-[#27272A] rounded-sm h-8 text-xs" /></div>
                    <Button onClick={handleAddOptions} className="w-full bg-white text-black hover:bg-gray-200 rounded-sm h-8 text-xs" data-testid="submit-options-btn">{editingOption ? "Save Changes" : "Add Position"}</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Positions Table — Enriched */}
          <div className="border overflow-x-auto" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            <table className="w-full dense-table" data-testid="stock-positions-table">
              <thead><tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Instrument</th>
                <th className="text-left text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Regime</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Qty</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Avg Cost</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Total Cost</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Price</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Mkt Value</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>P&L</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>P&L%</th>
                <th className="text-center text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Wheel</th>
                <th className="text-right text-[10px] font-medium" style={{ color: "var(--hoip-text-tertiary)" }}>Actions</th>
              </tr></thead>
              <tbody>
                {positions.stocks.map((p, i) => {
                  const rg = posRegimes[p.ticker];
                  const stColors = { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" };
                  return (
                  <tr key={`s-${i}`} className="border-b hover:bg-[#1A1A1A]" style={{ borderColor: "var(--hoip-divider)" }}>
                    <td className="font-mono-data text-[10px] font-medium">{p.ticker} <span className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Stock</span></td>
                    <td>{rg ? <span className="text-[8px] px-1 py-0.5 rounded-sm font-bold" style={{ color: stColors[rg.dominant_state_label], background: `${stColors[rg.dominant_state_label]}15` }}>{rg.dominant_state_label?.split(" ")[0]}</span> : <span className="text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</span>}</td>
                    <td className="text-right font-mono-data text-[10px]">{p.shares?.toLocaleString()}</td>
                    <td className="text-right font-mono-data text-[10px]">${p.avg_cost?.toFixed(2)}</td>
                    <td className="text-right font-mono-data text-[10px]">${p.total_cost?.toLocaleString()}</td>
                    <td className="text-right font-mono-data text-[10px]">${p.current_price?.toFixed(2)}</td>
                    <td className="text-right font-mono-data text-[10px]">${p.market_value?.toLocaleString()}</td>
                    <td className={`text-right font-mono-data text-[10px] ${(p.pnl||0) >= 0 ? "text-emerald-500" : "text-red-400"}`}>${p.pnl?.toLocaleString()}</td>
                    <td className={`text-right font-mono-data text-[10px] ${(p.pnl_pct||0) >= 0 ? "text-emerald-500" : "text-red-400"}`}>{p.pnl_pct >= 0 ? "+" : ""}{p.pnl_pct}%</td>
                    <td className="text-center">
                      {(() => {
                        const hasCC = positions.options.some(o => o.ticker === p.ticker && o.strategy_type === "covered_call");
                        const hasCSP = positions.options.some(o => o.ticker === p.ticker && ["cash_secured_put","bull_put_spread"].includes(o.strategy_type));
                        if (hasCC) return <span className="text-[8px] px-1 py-0.5 rounded-sm font-bold bg-blue-500/10 text-blue-400" data-testid={`wheel-stage-${p.ticker}`}>CC WRITING</span>;
                        if (hasCSP) return <span className="text-[8px] px-1 py-0.5 rounded-sm font-bold bg-emerald-500/10 text-emerald-400" data-testid={`wheel-stage-${p.ticker}`}>CSP ACTIVE</span>;
                        return <span className="text-[8px] px-1 py-0.5 rounded-sm font-bold bg-zinc-500/10 text-zinc-400" data-testid={`wheel-stage-${p.ticker}`}>IDLE</span>;
                      })()}
                    </td>
                    <td className="text-right"><div className="flex items-center justify-end gap-1"><button onClick={() => requirePro("/position/adjust", () => setAdjustTicker(p.ticker))} className="p-1 hover:bg-[#1A1A1A] rounded-sm" title={isPro === false ? "Roll / Hedge / Reduce / Close — Pro feature" : "Adjust"} data-testid={`adjust-stock-${p.ticker}`}>{isPro === false ? <Lock className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} /> : <ArrowRightLeft className="w-3 h-3 text-blue-400" />}</button><button onClick={() => editStock(p)} className="p-1 hover:bg-[#1A1A1A] rounded-sm" title="Edit" data-testid={`edit-stock-${p.ticker}`}><Pencil className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} /></button><button onClick={() => handleDeleteStock(p.id)} className="p-1 hover:bg-red-900/30 rounded-sm" data-testid={`delete-stock-${p.ticker}`}><Trash2 className="w-3 h-3 text-red-500" /></button></div></td>
                  </tr>
                  );
                })}
                {positions.options.map((p, i) => {
                  const rg = posRegimes[p.ticker];
                  const stColors = { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" };
                  const strikeLabel = [p.short_strike && `$${p.short_strike}`, p.long_strike && `$${p.long_strike}`].filter(Boolean).join("/");
                  const stratLabel = (p.strategy_type || "").replace(/_/g, " ");
                  const expiryShort = p.expiry ? p.expiry.slice(5) : "";
                  const instrumentLabel = `${p.ticker} ${expiryShort} ${strikeLabel} ${stratLabel}`.trim();
                  const isCredit = ["bull_put_spread","bear_call_spread","iron_condor","iron_butterfly","short_put","cash_secured_put","short_call","covered_call","strangle","straddle"].includes(p.strategy_type);
                  return (
                  <tr key={`o-${i}`} className="border-b hover:bg-[#1A1A1A]" style={{ borderColor: "var(--hoip-divider)" }}>
                    <td className="font-mono-data text-[10px]"><span className="font-medium">{p.ticker}</span> <span style={{ color: "var(--hoip-text-secondary)" }}>{expiryShort} {strikeLabel}</span> <span className="capitalize text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{stratLabel}</span></td>
                    <td>{rg ? <span className="text-[8px] px-1 py-0.5 rounded-sm font-bold" style={{ color: stColors[rg.dominant_state_label], background: `${stColors[rg.dominant_state_label]}15` }}>{rg.dominant_state_label?.split(" ")[0]}</span> : <span className="text-[8px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</span>}</td>
                    <td className="text-right font-mono-data text-[10px]">{isCredit ? `-${p.qty}` : p.qty}</td>
                    <td className="text-right font-mono-data text-[10px]">${p.premium?.toFixed(2)}</td>
                    <td className={`text-right font-mono-data text-[10px] ${(p.total_cost||0) < 0 ? "text-emerald-500" : ""}`}>{(p.total_cost||0) < 0 ? `+$${Math.abs(p.total_cost).toLocaleString()}` : `$${(p.total_cost||0).toLocaleString()}`}</td>
                    <td className="text-right font-mono-data text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</td>
                    <td className="text-right font-mono-data text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</td>
                    <td className="text-right font-mono-data text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</td>
                    <td className="text-right font-mono-data text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>—</td>
                    <td className="text-right"><div className="flex items-center justify-end gap-1"><button onClick={() => requirePro("/position/adjust", () => setAdjustTicker(p.ticker))} className="p-1 hover:bg-[#1A1A1A] rounded-sm" title={isPro === false ? "Roll / Hedge / Reduce / Close — Pro feature" : "Adjust"} data-testid={`adjust-option-${p.ticker}`}>{isPro === false ? <Lock className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} /> : <ArrowRightLeft className="w-3 h-3 text-blue-400" />}</button><button onClick={() => editOption(p)} className="p-1 hover:bg-[#1A1A1A] rounded-sm" title="Edit" data-testid={`edit-option-${p.ticker}`}><Pencil className="w-3 h-3" style={{ color: "var(--hoip-text-tertiary)" }} /></button><button onClick={() => handleDeleteOption(p.id)} className="p-1 hover:bg-red-900/30 rounded-sm" data-testid={`delete-option-${p.ticker}`}><Trash2 className="w-3 h-3 text-red-500" /></button></div></td>
                  </tr>
                  );
                })}
                {positions.stocks.length === 0 && positions.options.length === 0 && (<tr><td colSpan={10} className="text-center py-6 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>No positions. Add stocks or options above.</td></tr>)}
              </tbody>
            </table>
          </div>

          {/* Greeks & Sector */}
          {adjustTicker && (
            <AdjustPanel ticker={adjustTicker} onClose={() => { setAdjustTicker(null); load(); }} />
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
              <h3 className="text-sm font-bold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Portfolio Greeks</h3>
              <div className="space-y-3">
                {[{ label: "Net Delta", value: summary?.net_delta || 0 }, { label: "Net Theta", value: `$${(summary?.net_theta || 0).toFixed(2)}/day` }, { label: "Margin Used", value: `${summary?.margin_used_pct || 0}%` }].map(({ label, value }) => (
                  <div key={label} className="flex justify-between items-center border-b pb-2" style={{ borderColor: "var(--hoip-divider)" }}>
                    <span className="text-xs" style={{ color: "var(--hoip-text-secondary)" }}>{label}</span>
                    <span className="font-mono-data text-xs font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </div>
            {sectorData.length > 0 && (
              <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
                <h3 className="text-sm font-bold mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Sector Exposure</h3>
                <ResponsiveContainer width="100%" height={160}><PieChart><Pie data={sectorData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} stroke="var(--hoip-border)">{sectorData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}</Pie><Tooltip contentStyle={{ background: "#111111", border: "1px solid #27272A", fontSize: "12px" }} /></PieChart></ResponsiveContainer>
                <div className="flex flex-wrap gap-2 mt-2">{sectorData.map((s, i) => (<span key={i} className="text-xs flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: s.fill }}></span>{s.name}</span>))}</div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="pie" className="mt-4">
          <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Portfolio Intelligence Engine</span>
            </div>
            <PIESection />
            <div className="mt-4">
              <BrokerTruthReconciliation />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="rotation" className="mt-4">
          <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
            <div className="flex items-center gap-2 mb-4">
              <Compass className="w-4 h-4 text-blue-500" />
              <span className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Sector Rotation Monitor</span>
            </div>
            <SectorRotationPanel />
          </div>
        </TabsContent>

        <TabsContent value="rebalancing" className="mt-4">
          <RebalanceControls legacy={
            <div className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-4 h-4 text-blue-500" />
                <span className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>Sector &amp; Wheel-Aware Analysis</span>
              </div>
              <RebalancingPanel />
            </div>
          } />
        </TabsContent>
      </Tabs>
    </div>
  );
}
